import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  IconButton,
  FormControlLabel,
  Switch,
  Breadcrumbs,
  Link,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { ArrowBack, Save, AccessTime, History } from "@mui/icons-material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  createTimeSlot,
  updateTimeSlot,
  getServiceCategories,
  getAllTimeSlots,
} from "../../services/Api";

// Parse "09:00 AM" -> { hour: '09', minute: '00', meridiem: 'AM' }
const parseTime = (timeStr) => {
  if (!timeStr) return { hour: "12", minute: "00", meridiem: "AM" };
  // Handle 24h format "HH:MM"
  if (/^\d{1,2}:\d{2}$/.test(timeStr.trim())) {
    let [h, m] = timeStr.split(":").map(Number);
    const meridiem = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return {
      hour: String(h).padStart(2, "0"),
      minute: String(m).padStart(2, "0"),
      meridiem,
    };
  }
  // Handle "HH:MM AM/PM"
  const parts = timeStr.trim().split(" ");
  const [h, m] = (parts[0] || "12:00").split(":");
  return {
    hour: String(h).padStart(2, "0"),
    minute: String(m).padStart(2, "0"),
    meridiem: parts[1] || "AM",
  };
};

// Format { hour, minute, meridiem } -> "09:00 AM"
const formatTime = ({ hour, minute, meridiem }) =>
  `${hour}:${minute} ${meridiem}`;

// Custom 12-hour time picker component
const TimePicker = ({ label, value, onChange }) => {
  const parsed = parseTime(value);
  const [hour, setHour] = useState(parsed.hour);
  const [minute, setMinute] = useState(parsed.minute);
  const [meridiem, setMeridiem] = useState(parsed.meridiem);

  // Sync internal state when value prop changes (e.g., on edit load)
  useEffect(() => {
    const p = parseTime(value);
    setHour(p.hour);
    setMinute(p.minute);
    setMeridiem(p.meridiem);
  }, [value]);

  const handleChange = (newHour, newMinute, newMeridiem) => {
    const formatted = formatTime({
      hour: newHour,
      minute: newMinute,
      meridiem: newMeridiem,
    });
    onChange(formatted);
  };

  const hours = Array.from({ length: 12 }, (_, i) =>
    String(i + 1).padStart(2, "0"),
  );
  const minutes = Array.from({ length: 60 }, (_, i) =>
    String(i).padStart(2, "0"),
  );

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1, display: "block", fontWeight: 500 }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {/* Hour */}
        <FormControl size="small" sx={{ minWidth: 72 }}>
          <InputLabel>HH</InputLabel>
          <Select
            value={hour}
            label="HH"
            onChange={(e) => {
              setHour(e.target.value);
              handleChange(e.target.value, minute, meridiem);
            }}
            sx={{ borderRadius: 2 }}
          >
            {hours.map((h) => (
              <MenuItem key={h} value={h}>
                {h}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="h5" sx={{ color: "text.secondary", mb: 0.5 }}>
          :
        </Typography>

        {/* Minute */}
        <FormControl size="small" sx={{ minWidth: 72 }}>
          <InputLabel>MM</InputLabel>
          <Select
            value={minute}
            label="MM"
            onChange={(e) => {
              setMinute(e.target.value);
              handleChange(hour, e.target.value, meridiem);
            }}
            sx={{ borderRadius: 2 }}
          >
            {minutes.map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* AM/PM */}
        <ToggleButtonGroup
          value={meridiem}
          exclusive
          onChange={(_, val) => {
            if (val) {
              setMeridiem(val);
              handleChange(hour, minute, val);
            }
          }}
          size="small"
          sx={{ height: 40 }}
        >
          <ToggleButton
            value="AM"
            sx={{ px: 1.5, fontWeight: 600, borderRadius: "8px 0 0 8px" }}
          >
            AM
          </ToggleButton>
          <ToggleButton
            value="PM"
            sx={{ px: 1.5, fontWeight: 600, borderRadius: "0 8px 8px 0" }}
          >
            PM
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </Box>
  );
};

const ModifyTimeSlots = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    startSlot: "09:00 AM",
    endSlot: "11:00 AM",
    date: "",
    isActive: true,
    moduleType: "Grocery",
    categoryId: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  const today = new Date();
  const todayDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  useEffect(() => {
    if (isEdit && location.state?.slotData) {
      const slotData = location.state.slotData;
      let startSlot = "09:00 AM";
      let endSlot = "11:00 AM";

      if (slotData.startSlot && slotData.endSlot) {
        startSlot = slotData.startSlot;
        endSlot = slotData.endSlot;
      } else if (slotData.slot) {
        const parts = slotData.slot.split(" - ");
        startSlot = parts[0]?.trim() || startSlot;
        endSlot = parts[1]?.trim() || endSlot;
      }

      const fetchedModuleType = slotData.moduleType || "Grocery";
      const isService = fetchedModuleType !== "Grocery";

      setFormData({
        startSlot,
        endSlot,
        date: slotData.date || "",
        isActive: slotData.isActive ?? true,
        moduleType: isService ? "Service" : "Grocery",
        categoryId: slotData.categoryId || "",
      });
    } else if (isEdit && !location.state?.slotData) {
      setError(
        "Time slot data missing. Please go back and select a slot to edit.",
      );
    }
  }, [id, isEdit, location.state]);

  useEffect(() => {
    if (formData.moduleType === "Service") {
      fetchServiceCategories();
    }
  }, [formData.moduleType]);

  useEffect(() => {
    if (isEdit && serviceCategories.length > 0 && location.state?.slotData) {
      const fetchedModuleType = location.state.slotData.moduleType;
      if (fetchedModuleType && fetchedModuleType !== "Grocery" && fetchedModuleType !== "Service") {
        const cat = serviceCategories.find(
          (c) => (c.name || c.categoryName) === fetchedModuleType
        );
        if (cat) {
          setFormData((prev) => {
            if (!prev.categoryId) {
              return { ...prev, categoryId: cat.id || cat._id };
            }
            return prev;
          });
        }
      }
    }
  }, [serviceCategories, isEdit, location.state]);

  const fetchServiceCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await getServiceCategories(1, 100);
      if (response.success || response.data) {
        setServiceCategories(response.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch service categories:", err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "isActive" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const payload = {
      startSlot: formData.startSlot,
      endSlot: formData.endSlot,
      date: formData.date,
      isActive: formData.isActive,
    };

    // Set moduleType based on selection
    if (formData.moduleType === "Service" && formData.categoryId) {
      // When Service is selected and a category is chosen, pass category name as moduleType
      const selectedCategory = serviceCategories.find(
        (cat) => (cat.id || cat._id) === formData.categoryId,
      );
      payload.moduleType = selectedCategory
        ? selectedCategory.name || selectedCategory.categoryName
        : "Service";
    } else {
      // For Grocery or when no category is selected, pass the moduleType directly
      payload.moduleType = formData.moduleType;
    }

    // Check for duplicate time slots
    try {
      const listRes = await getAllTimeSlots();
      const dataSrc = listRes.data || listRes;
      const existingSlots = Array.isArray(dataSrc) ? dataSrc : [];

      const isDuplicate = existingSlots.some((slot) => {
        const matchesDate = (slot.date || "") === formData.date;

        let slotStart = slot.startSlot || "";
        let slotEnd = slot.endSlot || "";
        if (!slotStart && !slotEnd && slot.slot) {
          const parts = slot.slot.split(" - ");
          slotStart = parts[0]?.trim() || "";
          slotEnd = parts[1]?.trim() || "";
        }

        const matchesStart = slotStart === formData.startSlot;
        const matchesEnd = slotEnd === formData.endSlot;
        const matchesModule = (slot.moduleType || "") === payload.moduleType;
        const isDifferentId =
          !isEdit || String(slot.id || slot._id) !== String(id);

        return (
          matchesDate &&
          matchesStart &&
          matchesEnd &&
          matchesModule &&
          isDifferentId
        );
      });

      if (isDuplicate) {
        setError(
          "Time slot already exists for this date, time interval, and module type.",
        );
        setSubmitting(false);
        return;
      }
    } catch (err) {
      console.error("Failed to validate duplicates:", err);
    }

    try {
      let response;
      if (isEdit) {
        response = await updateTimeSlot(id, payload);
      } else {
        response = await createTimeSlot(payload);
      }

      if (response.success || response.data || response.id) {
        navigate("/timeslots/all");
      } else {
        setError(
          response.message ||
            `Failed to ${isEdit ? "update" : "create"} time slot`,
        );
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          `Error ${isEdit ? "updating" : "creating"} time slot`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const slotPreview =
    formData.startSlot && formData.endSlot
      ? `${formData.startSlot} - ${formData.endSlot}`
      : "Select both times above";

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
          <Link
            underline="hover"
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate("/timeslots/all");
            }}
          >
            Time Slots
          </Link>
          <Typography color="text.primary">
            {isEdit ? "Edit Slot" : "Create Slot"}
          </Typography>
        </Breadcrumbs>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            onClick={() => navigate("/timeslots/all")}
            sx={{ border: "1px solid", borderColor: "divider" }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {isEdit ? "Edit Time Slot" : "Create New Time Slot"}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}
              >
                <AccessTime color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Slot Configuration
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Date"
                    name="date"
                    type="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: todayDate }}
                    helperText="Select the date for this slot"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>

                <Grid size={{ xs: 6 }}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Module Type</InputLabel>
                    <Select
                      name="moduleType"
                      value={formData.moduleType}
                      onChange={handleInputChange}
                      label="Module Type"
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    >
                      <MenuItem value="Grocery">Grocery</MenuItem>
                      <MenuItem value="Service">Service</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {formData.moduleType === "Service" && (
                  <Grid size={{ xs: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Service Category</InputLabel>
                      <Select
                        name="categoryId"
                        value={formData.categoryId}
                        onChange={handleInputChange}
                        label="Service Category"
                        disabled={loadingCategories}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      >
                        {loadingCategories ? (
                          <MenuItem disabled>Loading categories...</MenuItem>
                        ) : serviceCategories.length > 0 ? (
                          serviceCategories.map((category) => (
                            <MenuItem
                              key={category.id || category._id}
                              value={category.id || category._id}
                            >
                              {category.name || category.categoryName}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled>No categories available</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <br />

                {/* Start Time Picker */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TimePicker
                    label="Start Time"
                    value={formData.startSlot}
                    onChange={(val) =>
                      setFormData((prev) => ({ ...prev, startSlot: val }))
                    }
                  />
                </Grid>

                {/* End Time Picker */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TimePicker
                    label="End Time"
                    value={formData.endSlot}
                    onChange={(val) =>
                      setFormData((prev) => ({ ...prev, endSlot: val }))
                    }
                  />
                </Grid>

                {/* Live Preview */}
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "rgba(0,0,0,0.02)",
                      borderRadius: 2,
                      border: "1px dashed",
                      borderColor: "divider",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <AccessTime fontSize="small" color="action" />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mr: 1 }}
                    >
                      Preview:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {slotPreview}
                    </Typography>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        name="isActive"
                        color="primary"
                      />
                    }
                    label={
                      formData.isActive
                        ? "Slot is Active (Visible to users)"
                        : "Slot is Inactive (Hidden)"
                    }
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                mb: 3,
              }}
            >
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <History color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Instructions
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Select hours, minutes, and AM/PM for both the start and end
                  time. The combined slot label will appear in the preview box.
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  <strong>Module Type:</strong> Choose "Grocery" for grocery
                  slots or "Service" for service-based slots.
                </Typography>
                {formData.moduleType === "Service" && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    <strong>Service Category:</strong> Select a specific service
                    category when "Service" module type is chosen.
                  </Typography>
                )}
                <Divider sx={{ mb: 2 }} />
                <Typography variant="caption" color="text.secondary">
                  Times are saved in 12-hour format (e.g., "09:00 AM - 11:00
                  AM").
                </Typography>
              </CardContent>
            </Card>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "rgba(0,0,0,0.01)",
              }}
            >
              <Button
                fullWidth
                variant="contained"
                size="large"
                type="submit"
                disabled={submitting}
                startIcon={
                  submitting ? <CircularProgress size={20} /> : <Save />
                }
                sx={{ borderRadius: 2, height: 48, fontWeight: 700 }}
              >
                {submitting
                  ? "Processing..."
                  : isEdit
                    ? "Update Time Slot"
                    : "Save Time Slot"}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={() => navigate("/timeslots/all")}
                sx={{ mt: 2, borderRadius: 2, height: 48 }}
              >
                Cancel
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default ModifyTimeSlots;
